/**
 * SG AGM Exempted AFS — 엔티티 Profile 기반 폼 자동 채움.
 */
import { dateToHtmlInput } from '../../../change-financial-year-end/composables/dateToHtmlInput';
import type { AgmExemptedAfsFormModel } from './useFormPayload';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function unwrapEntityProfile(profileRaw: Record<string, any>): Record<string, any> {
  let p = profileRaw;
  for (let i = 0; i < 2; i += 1) {
    const inner = p?.data;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      const hasProfileShape =
        inner.accountingDates != null
        || inner.regNo != null
        || inner.REG_NO != null;
      if (hasProfileShape) {
        p = inner as Record<string, any>;
        continue;
      }
    }
    break;
  }
  return p;
}

function yearAfterFye(fyeHtml: string): string {
  const m = /^(\d{4})-\d{2}-\d{2}$/.exec(fyeHtml);
  if (!m) return '';
  return String(Number(m[1]) + 1);
}

function resolveFyeFromProfile(profileRaw: Record<string, any> | null | undefined): string {
  if (!profileRaw || typeof profileRaw !== 'object') return '';
  const profile = unwrapEntityProfile(profileRaw);
  const accounting = (profile.accountingDates as Record<string, any>) || {};
  return dateToHtmlInput(
    accounting.financialYearEnd
    ?? profile.financialYearEnd
    ?? profile.FINANCIAL_YEAR_END,
  );
}

function resolveAgmDueFromProfile(profileRaw: Record<string, any> | null | undefined): string {
  if (!profileRaw || typeof profileRaw !== 'object') return '';
  const profile = unwrapEntityProfile(profileRaw);
  const accounting = (profile.accountingDates as Record<string, any>) || {};
  return dateToHtmlInput(
    accounting.nextAGMDueDate ?? profile.agmDuedate ?? profile.AGM_DUEDATE,
  );
}

function resolveFyeFromEntityList(listEnt: Record<string, any> | null | undefined): string {
  if (!listEnt || typeof listEnt !== 'object') return '';
  return dateToHtmlInput(listEnt.financialYearEnd ?? listEnt.FINANCIAL_YEAR_END);
}

function resolveAgmDueFromEntityList(listEnt: Record<string, any> | null | undefined): string {
  if (!listEnt || typeof listEnt !== 'object') return '';
  return dateToHtmlInput(
    listEnt.nextAGMDueDate ?? listEnt.agmDuedate ?? listEnt.AGM_DUEDATE,
  );
}

/** Profile 우선, 없으면 엔티티 목록 값 — 둘 다 없으면 빈 문자열(이전 회사 값 유지 안 함) */
function resolveEntityDates(
  profileRaw: Record<string, any> | null | undefined,
  listEnt?: Record<string, any> | null,
): { financialYearEndDate: string; annualReturnYear: string; deemedAgmDate: string } {
  const fye = resolveFyeFromProfile(profileRaw) || resolveFyeFromEntityList(listEnt);
  const deemedAgmDate = resolveAgmDueFromProfile(profileRaw) || resolveAgmDueFromEntityList(listEnt);
  return {
    financialYearEndDate: fye,
    annualReturnYear: fye ? yearAfterFye(fye) : '',
    deemedAgmDate,
  };
}

export function applyEntityProfileToAgmExemptedAfsForm(
  model: AgmExemptedAfsFormModel,
  profileRaw: Record<string, any> | null | undefined,
  listEnt?: Record<string, any> | null,
): void {
  const dates = resolveEntityDates(profileRaw, listEnt);
  model.financialYearEndDate = dates.financialYearEndDate;
  model.annualReturnYear = dates.annualReturnYear;
  model.deemedAgmDate = dates.deemedAgmDate;
}
